<script lang="ts" setup>
import AuthLayout from '@/layouts/AuthLayout.vue';
import ReportFilter from "@/components/features/reports/ReportFilter.vue";
import SalesByQualityReportDialog from "@/components/features/reports/SalesByQualityReportDialog.vue";
import { getSalesByQuality } from '@/api/reports';
import { getProductList } from '@/api/products';
import { ref, type Ref, onMounted, computed, watch } from "vue";
import ProgressSpinner from 'primevue/progressspinner';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import type { SalesByQualityReportResponse, SalesByQualityReportParams } from '@/types/report-types';
import type { Product } from '@/types/product-types';
import { CurrencyType } from '@/types/currency-types';

const toast = useToast();

const selectedQualityId = ref<string | null>(null);
const selectedCurrency = ref<number>(0);
const startDate = ref<Date | null>(null);
const endDate = ref<Date | null>(null);

const reportData: Ref<SalesByQualityReportResponse | null> = ref(null);
const isLoading = ref(false);
const isLoadingQualities = ref(false);
const errorMessage = ref<string | null>(null);
const showReportDialog = ref(false);

const fetchedProducts = ref<Product[]>([]);

const qualityOptions = computed(() => {
  const options: { label: string; value: string }[] = [];

  if (fetchedProducts.value.length > 0) {
    fetchedProducts.value
        .slice()
        .sort((a, b) => {
          const refA = a.finishedReference || a.unfinishedReference || '';
          const refB = b.finishedReference || b.unfinishedReference || '';
          return refA.localeCompare(refB);
        })
        .forEach(product => {
          if (product.id) {
            const displayName = product.finishedReference || product.unfinishedReference || 'Unknown Quality';
            options.push({ label: displayName, value: String(product.id) });
          }
        });
  }
  return options;
});

const currencyOptions = computed(() => {
  return CurrencyType.value.map(currency => ({
    label: `${currency.name} (${currency.symbol})`,
    value: currency.id
  }));
});

onMounted(async () => {
  isLoadingQualities.value = true;
  try {
    fetchedProducts.value = await getProductList();
    if (qualityOptions.value.length > 0 && !selectedQualityId.value) {
      selectedQualityId.value = qualityOptions.value[0].value;
    }
  } catch (error: any) {
    console.error("Error fetching quality options:", error);
    toast.add({ severity: 'error', summary: 'Error', detail: 'Failed to load quality options.', life: 3000 });
  } finally {
    isLoadingQualities.value = false;
  }
});

watch(qualityOptions, (newOptions) => {
  if (newOptions.length > 0 && selectedQualityId.value === null) {
    selectedQualityId.value = newOptions[0].value;
  } else if (newOptions.length === 0) {
    selectedQualityId.value = null;
  }
}, { immediate: true });

const handleGenerateReport = async () => {
  if (!startDate.value || !endDate.value) {
    toast.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please select both a start and end date.', life: 3000 });
    return;
  }
  if (!selectedQualityId.value) {
    toast.add({ severity: 'warn', summary: 'Validation Error', detail: 'Please select a quality.', life: 3000 });
    return;
  }

  isLoading.value = true;
  errorMessage.value = null;
  reportData.value = null;

  try {
    const params: SalesByQualityReportParams = {
      startDate: startDate.value.toISOString(),
      endDate: endDate.value.toISOString(),
      qualityId: selectedQualityId.value,
      currency: selectedCurrency.value,
    };

    const data = await getSalesByQuality(params);

    if (data && data.sales) {
      reportData.value = data;
      if (data.sales.length > 0) {
        showReportDialog.value = true;
      } else {
        toast.add({ severity: 'info', summary: 'No Data', detail: 'No sales data found for the selected quality and date range.', life: 4000 });
        showReportDialog.value = false;
      }
    } else {
      reportData.value = null;
      toast.add({ severity: 'info', summary: 'No Data', detail: 'No sales data found for the selected criteria (empty response).', life: 4000 });
      showReportDialog.value = false;
    }

  } catch (error: any) {
    console.error("Error fetching Sales by Quality report:", error);
    const apiErrorMessage = error.response?.data?.title || error.response?.data?.message || error.message || "Failed to fetch report data.";
    errorMessage.value = apiErrorMessage;
    toast.add({ severity: 'error', summary: 'API Error', detail: apiErrorMessage, life: 5000 });
    showReportDialog.value = false;
  } finally {
    isLoading.value = false;
  }
};

const handleCloseReportDialog = () => {
  showReportDialog.value = false;
};
</script>

<template>
  <AuthLayout>
    <div class="min-h-screen p-4 md:p-6">
      <ReportFilter
          title="Sales by Quality"
          dropdownLabel="Quality"
          v-model="selectedQualityId"
          :dropdownOptions="qualityOptions"
          :disabled="isLoadingQualities || qualityOptions.length === 0"
          placeholder="Select a Quality"
          :showCurrencyFilter="true"
          v-model:currency="selectedCurrency"
          :currencyOptions="currencyOptions"
          @update:startDate="startDate = $event"
          @update:endDate="endDate = $event"
          @generate-report="handleGenerateReport"
      />

      <div v-if="isLoading || isLoadingQualities" class="flex justify-center items-center mt-10">
        <ProgressSpinner strokeWidth="4" style="width: 50px; height: 50px" animationDuration=".5s"/>
        <p class="ml-3 text-lg text-gray-600">
          {{ isLoadingQualities ? 'Loading quality options...' : 'Generating Report...' }}
        </p>
      </div>

      <div v-if="!isLoadingQualities && qualityOptions.length === 0 && !isLoading" class="mt-6 text-center text-orange-600">
        <p>No qualities available to generate a report. Please ensure products exist in the system.</p>
      </div>

      <Message v-if="errorMessage && !showReportDialog" severity="error" :closable="true" class="mt-6">
        {{ errorMessage }}
      </Message>

      <div v-if="!isLoading && !isLoadingQualities && !reportData && !errorMessage && !showReportDialog && qualityOptions.length > 0" class="mt-10 text-center text-gray-500">
        <p>Select your criteria and click "Create Report" to view details.</p>
      </div>

      <SalesByQualityReportDialog
          :visible="showReportDialog"
          :reportData="reportData"
          :startDate="startDate"
          :endDate="endDate"
          :qualityId="selectedQualityId"
          :currency="selectedCurrency"
          :onClose="handleCloseReportDialog"
      />
    </div>
  </AuthLayout>
</template>
